import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CalendarDays, Eye, GitCompare, Layers, Bell, Edit, Check,
  Clock, Users, ChevronLeft, ChevronRight, GripVertical
} from 'lucide-react';
import { ComingSoonBanner } from '../ComingSoonBanner';

// Types
interface Employee {
  id: number;
  name: string;
  department: string;
  role: string;
  availability: string;
  skills: string[];
}

interface ShiftTemplate {
  id: number;
  name: string;
  startTime: string;
  endTime: string;
  duration: number;
  color: string;
  description: string;
}

// Mock data
const mockEmployees: Employee[] = [
  { id: 1, name: 'Sarah Johnson', department: 'HR', role: 'Manager', availability: 'available', skills: ['Management', 'Training'] },
  { id: 2, name: 'Mike Chen', department: 'IT', role: 'Developer', availability: 'available', skills: ['React', 'Node.js', 'Database'] },
  { id: 3, name: 'Lisa Wang', department: 'Sales', role: 'Representative', availability: 'limited', skills: ['Customer Service', 'Sales'] },
  { id: 4, name: 'James Brown', department: 'Operations', role: 'Manager', availability: 'unavailable', skills: ['Leadership', 'Operations'] },
];

const mockShiftTemplates: ShiftTemplate[] = [
  { id: 1, name: 'Morning Shift', startTime: '08:00', endTime: '16:00', duration: 8, color: '#3B82F6', description: 'Standard morning shift' },
  { id: 2, name: 'Evening Shift', startTime: '16:00', endTime: '00:00', duration: 8, color: '#F59E0B', description: 'Evening shift coverage' },
  { id: 3, name: 'Night Shift', startTime: '00:00', endTime: '08:00', duration: 8, color: '#8B5CF6', description: 'Overnight shift' },
];

const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const getAvailabilityColor = (availability: string) => {
  switch (availability) {
    case 'available': return 'bg-green-100 text-green-700';
    case 'limited': return 'bg-yellow-100 text-yellow-700';
    case 'unavailable': return 'bg-red-100 text-red-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

export default function DutyRosterManagement() {
  const { t } = useTranslation();
  const [currentView, setCurrentView] = useState('weekly');

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <ComingSoonBanner
        icon={<CalendarDays className="w-6 h-6" />}
        title="Duty Roster Management"
        description="Duty Roster Management is currently in development. This is a preview of the upcoming scheduling system."
        gradientFrom="from-orange-500"
        gradientTo="to-red-500"
      />

      {/* Header Section */}
      <div className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-indigo-500/10 border border-border rounded-xl p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg mr-4">
                <CalendarDays size={24} color="white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{t('Duty Roster Management')}</h1>
                <p className="text-gray-500">{t('Create, assign, and manage employee duty schedules with drag-and-drop functionality')}</p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center space-x-3">
            <button className={`flex items-center space-x-2 px-4 py-2 bg-white border border-border text-gray-700 rounded-lg`}>
              <GitCompare size={16} />
              <span className="text-sm font-medium">{t('Compare Attendance')}</span>
            </button>
            <button className={`flex items-center space-x-2 px-4 py-2 bg-white border border-border text-gray-700 rounded-lg`}>
              <Layers size={16} />
              <span className="text-sm font-medium">{t('Bulk Operations')}</span>
            </button>
            <button className={`flex items-center space-x-2 px-4 py-2 bg-white border border-border text-gray-700 rounded-lg`}>
              <Bell size={16} />
              <span className="text-sm font-medium">{t('Notifications')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Panel - Tools & Employee Selection */}
        <div className="lg:col-span-1 space-y-6">
          {/* View Toggle */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Eye size={18} className="mr-2" />
              {t('Calendar View')}
            </h3>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setCurrentView('weekly')}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all ${
                  currentView === 'weekly' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {t('Weekly')}
              </button>
              <button
                onClick={() => setCurrentView('monthly')}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all ${
                  currentView === 'monthly' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {t('Monthly')}
              </button>
            </div>
          </div>

          {/* Employee Selector */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Users size={18} className="mr-2" />
              {t('Employees')}
            </h3>
            <div className="space-y-3">
              {mockEmployees.map((employee) => (
                <div key={employee.id} className="flex items-center space-x-3 p-3 border border-border rounded-lg">
                  <GripVertical size={14} className="text-gray-600" />
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {employee.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{employee.name}</p>
                    <p className="text-xs text-gray-500">{employee.department} - {employee.role}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getAvailabilityColor(employee.availability)}`}>
                    {employee.availability}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Shift Template Library */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Clock size={18} className="mr-2" />
              {t('Shift Templates')}
            </h3>
            <div className="space-y-3">
              {mockShiftTemplates.map((template) => (
                <div
                  key={template.id}
                  className="flex items-center space-x-3 p-3 border border-border rounded-lg"
                >
                  <div className="w-3 h-8 rounded" style={{ backgroundColor: template.color }} />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{template.name}</p>
                    <p className="text-xs text-gray-500">
                      {template.startTime} - {template.endTime} ({template.duration}h)
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content - Calendar Scheduler */}
        <div className="lg:col-span-3">
          <div className="bg-card border border-border rounded-xl">
            {/* Calendar Header */}
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button className={`p-2 rounded-lg border border-border`}>
                  <ChevronLeft size={16} />
                </button>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('January 2025')} - {t('Week')} 4
                </h3>
                <button className={`p-2 rounded-lg border border-border`}>
                  <ChevronRight size={16} />
                </button>
              </div>
              <button className={`flex items-center gap-2 px-3 py-1.5 text-sm border border-border rounded-lg`}>
                <Edit size={14} />
                {t('Edit Schedule')}
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-32">{t('Employee')}</th>
                    {weekDays.map((day) => (
                      <th key={day} className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t(day)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {mockEmployees.map((employee) => (
                    <tr key={employee.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <div className="w-7 h-7 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                            {employee.name.charAt(0)}
                          </div>
                          <span className="text-sm font-medium text-gray-900 truncate max-w-[80px]">{employee.name.split(' ')[0]}</span>
                        </div>
                      </td>
                      {weekDays.map((day, idx) => {
                        // Mock schedule assignments
                        const hasShift = (employee.id + idx) % 3 !== 0;
                        const shiftIdx = (employee.id + idx) % 3;
                        const shift = hasShift ? mockShiftTemplates[shiftIdx] : null;
                        return (
                          <td key={day} className="px-1 py-2 text-center">
                            {shift ? (
                              <div
                                className="mx-auto px-2 py-1 rounded text-xs font-medium text-white max-w-[80px] truncate"
                                style={{ backgroundColor: shift.color }}
                              >
                                {shift.name.split(' ')[0]}
                              </div>
                            ) : (
                              <div className="mx-auto w-8 h-6 bg-gray-100 rounded" />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Legend */}
            <div className="p-4 border-t border-border flex items-center space-x-6">
              {mockShiftTemplates.map((template) => (
                <div key={template.id} className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: template.color }} />
                  <span className="text-xs text-gray-600">{template.name}</span>
                </div>
              ))}
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded bg-gray-200" />
                <span className="text-xs text-gray-600">{t('Off / Unassigned')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Edit Button */}
      <div className="fixed bottom-6 right-6">
        <button
          className={`w-14 h-14 bg-blue-400 text-white rounded-full shadow-lg flex items-center justify-center`}
         
        >
          <Edit size={20} />
        </button>
      </div>
    </div>
  );
}
